import { useCallback, useEffect, useMemo, useState } from 'react';
import { MdPayments, MdSearch } from 'react-icons/md';
import PageHeader from '../../components/owner/PageHeader';
import { formatCurrency } from './receptionistData';
import { fetchRemainingPayments } from '../../services/receptionist/receptionistApi';
import { getGymMemberId, isPendingGymMemberId } from '../admin/members/memberData';
import { PAYMENTS_UPDATE_EVENT } from './paymentStore';
import {
  REMAINING_PAYMENT_CATEGORY_ORDER,
  groupPendingByCategory,
  remainingPaymentCategory,
} from './sales/remainingPaymentCategories';
import {
  saleRowLeft,
  saleRowPaid,
  saleRowTotal,
  saleRowHasRemainingBalance,
  isPaymentWalkIn,
} from './sales/salePaymentUtils';
import { saleRowEndDate, saleRowStartDate, saleRowDuration, saleRowPackageName } from './sales/salePeriodUtils';
import '../owner/ownerPages.css';
import '../owner/finance/finance.css';
import './members/members.css';
import './sales.css';
import './sales/ancillarySales.css';
import './remainingPayments.css';

function saleDate(row) {
  return String(row.date || '').slice(0, 10) || '—';
}

export default function RemainingPaymentsReport() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const loadData = useCallback(async () => {
    try {
      setError('');
      setLoading(true);
      // Always load remaining balances fresh from Supabase
      const rows = await fetchRemainingPayments();
      setPayments(rows || []);
    } catch (err) {
      setError(err.message || 'Failed to load remaining payments report');
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const refresh = () => loadData();
    window.addEventListener(PAYMENTS_UPDATE_EVENT, refresh);
    return () => window.removeEventListener(PAYMENTS_UPDATE_EVENT, refresh);
  }, [loadData]);

  const pendingPayments = useMemo(
    () => (payments || [])
      .filter((row) => saleRowHasRemainingBalance(row))
      .sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))),
    [payments],
  );

  const filteredPending = useMemo(() => {
    const q = search.trim().toLowerCase();
    return pendingPayments.filter((row) => {
      const category = remainingPaymentCategory(row);
      if (categoryFilter !== 'all' && category !== categoryFilter) return false;
      if (!q) return true;
      return (
        String(row.member || '').toLowerCase().includes(q)
        || String(row.gymId || '').toLowerCase().includes(q)
        || String(row.package || '').toLowerCase().includes(q)
        || String(row.paymentNote || '').toLowerCase().includes(q)
        || category.toLowerCase().includes(q)
        || saleDate(row).includes(q)
      );
    });
  }, [pendingPayments, search, categoryFilter]);

  const groupedByCategory = useMemo(
    () => groupPendingByCategory(filteredPending, saleRowLeft),
    [filteredPending],
  );

  const totalLeftAll = useMemo(
    () => filteredPending.reduce((sum, row) => sum + saleRowLeft(row), 0),
    [filteredPending],
  );

  const categoryOptions = useMemo(() => {
    const present = new Set(pendingPayments.map(remainingPaymentCategory));
    return REMAINING_PAYMENT_CATEGORY_ORDER.filter((cat) => present.has(cat));
  }, [pendingPayments]);

  return (
    <div className="rec-ancillary-sale rec-remaining-payments">
      <PageHeader
        title="Remaining Payments Report"
        subtitle="All pending balances grouped by sale category"
      >
        <span className="owner-badge owner-badge-warning">
          {filteredPending.length} pending · {formatCurrency(totalLeftAll)} left
        </span>
      </PageHeader>

      {loading && <p className="owner-placeholder-text owner-mb-16">Loading…</p>}
      {error && (
        <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--owner-danger, #dc2626)' }}>
          {error}
        </p>
      )}

      <div className="rec-remaining-toolbar">
        <div className="rec-member-search-field">
          <MdSearch className="rec-member-search-icon" />
          <input
            type="text"
            className="owner-form-input rec-member-search-input"
            placeholder="Search member, serial no, package, category…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="owner-form-group" style={{ margin: 0, minWidth: 200 }}>
          <label className="owner-form-label" htmlFor="remaining-report-category">Category</label>
          <select
            id="remaining-report-category"
            className="owner-form-select"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="all">All categories</option>
            {categoryOptions.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {!loading && groupedByCategory.length === 0 && (
        <div className="owner-card rec-panel-card">
          <p className="owner-placeholder-text" style={{ padding: 24, margin: 0 }}>
            {pendingPayments.length === 0
              ? 'No remaining payments. All customers are fully paid.'
              : 'No pending payments match your search or category filter.'}
          </p>
        </div>
      )}

      {groupedByCategory.map(({ category, rows, totalLeft, count }) => (
        <div
          key={category}
          className="owner-card rec-panel-card rec-member-list-card rec-remaining-category-card"
        >
          <div className="rec-ancillary-sale-history-header">
            <h2 className="owner-card-title">
              <MdPayments style={{ marginRight: 8, verticalAlign: 'middle' }} />
              {category}
            </h2>
            <p className="owner-placeholder-text" style={{ margin: 0 }}>
              {count} sale{count === 1 ? '' : 's'} · {formatCurrency(totalLeft)} pending
            </p>
          </div>
          <div className="owner-table-wrap">
            <table className="owner-table rec-member-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Serial No</th>
                  <th>Member</th>
                  <th>Type</th>
                  <th>Sale / Package</th>
                  <th>Duration</th>
                  <th>Total</th>
                  <th>Paid</th>
                  <th>Left</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Invoice</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.rawId || row.id}>
                    <td>{saleDate(row)}</td>
                    <td className="rec-member-gym-id">
                      {row.isWalkIn || isPaymentWalkIn(row) || isPendingGymMemberId(row.gymId)
                        ? '—'
                        : (getGymMemberId(row) || row.gymId || '—')}
                    </td>
                    <td>{row.member || '—'}</td>
                    <td>{remainingPaymentCategory(row)}</td>
                    <td>{saleRowPackageName(row)}</td>
                    <td>{saleRowDuration(row)}</td>
                    <td>{formatCurrency(saleRowTotal(row))}</td>
                    <td className="finance-amount-positive">{formatCurrency(saleRowPaid(row))}</td>
                    <td className="finance-amount-negative">{formatCurrency(saleRowLeft(row))}</td>
                    <td>{saleRowStartDate(row)}</td>
                    <td>{saleRowEndDate(row)}</td>
                    <td>{row.invoiceId || '—'}</td>
                    <td>{row.paymentNote || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
